const maxHeight = {
    name: "max-height",
    path: "/max-height",
    title: "max-height",
    description: "Utilities for controlling the maximum width of elements.",
    value: {
        "max-height": {
            "max-h-<number>": "calc(var(--1) * <number>)",
            "max-h-<fraction>": "calc(<fraction> * 100%)",
            "max-h-3xs": "var(--container-3xs); /* 16rem (256px) */",
            "max-h-2xs": "var(--container-2xs); /* 18rem (288px) */",
            "max-h-xs": "var(--container-xs); /* 20rem (320px) */",
            "max-h-sm": "var(--container-sm); /* 24rem (384px) */",
            "max-h-md": "var(--container-md); /* 28rem (448px) */",
            "max-h-lg": "var(--container-lg); /* 32rem (512px) */",
            "max-h-xl": "var(--container-xl); /* 36rem (576px) */",
            "max-h-2xl": "var(--container-2xl); /* 42rem (672px) */",
            "max-h-3xl": "var(--container-3xl); /* 48rem (768px) */",
            "max-h-4xl": "var(--container-4xl); /* 56rem (896px) */",
            "max-h-5xl": "var(--container-5xl); /* 64rem (1024px) */",
            "max-h-6xl": "var(--container-6xl); /* 72rem (1152px) */",
            "max-h-7xl": "var(--container-7xl); /* 80rem (1280px) */",
            "max-h-auto": "auto",
            "max-h-px": "1px",
            "max-h-full": "100%",
            "max-h-screen": "100vw",
            "max-h-dvw": "100dvw",
            "max-h-dvh": "100dvh",
            "max-h-lvw": "100lvw",
            "max-h-lvh": "100lvh",
            "max-h-svw": "100svw",
            "max-h-svh": "100svh",
            "max-h-min": "min-content",
            "max-h-max": "max-content",
            "max-h-fit": "fit-content",
            "max-h-[<value>]": "<value>",
        },
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="max-h-96 bg-color-blue-500 rounded-lg p-2 text-center">max-h-96</div>
<div class="max-h-80 bg-color-blue-500 rounded-lg p-2 text-center">max-h-80</div>
<div class="max-h-64 bg-color-blue-500 rounded-lg p-2 text-center">max-h-64</div>
<div class="max-h-48 bg-color-blue-500 rounded-lg p-2 text-center">max-h-48</div>
<div class="max-h-40 bg-color-blue-500 rounded-lg p-2 text-center">max-h-40</div>
<div class="max-h-32 bg-color-blue-500 rounded-lg p-2 text-center">max-h-32</div>
<div class="max-h-24 bg-color-blue-500 rounded-lg p-2 text-center">max-h-24</div>
                `
            }
        },
        {
            "example": {
                "code": `
<div class="flex gap-4 text-color-white font-mono font-bold">
  <div class="max-max-h-full bg-color-blue-500 rounded-lg p-2 text-center">max-h-full</div>
  <div class="max-h-9/10 bg-color-blue-500 rounded-lg p-2 text-center">max-h-9/10</div>
  <div class="max-h-3/4 bg-color-blue-500 rounded-lg p-2 text-center">max-h-3/4</div>
  <div class="max-h-1/2 bg-color-blue-500 rounded-lg p-2 text-center">max-h-1/2</div>
  <div class="max-h-1/3 bg-color-blue-500 rounded-lg p-2 text-center">max-h-1/3</div>
</div>
                `
            }
        }
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">max-h-&lt;number&gt;</code> utilities to set fixed maximum width values:
            </p>
            <div class="flex gap-4 justify-center text-color-white font-mono font-bold relative h-96 items-end">
                <div class="absolute inset-0 z-0 !mb-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="relative max-h-96 h-full bg-color-blue-500 rounded-lg p-2 text-center flex items-end">
                    <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-96</div>
                </div>
                <div class="relative max-h-80 h-full bg-color-blue-500 rounded-lg p-2 text-center flex items-end">
                    <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-80</div>
                </div>
                <div class="relative max-h-64 h-full bg-color-blue-500 rounded-lg p-2 text-center flex items-end">
                <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-64</div>
                </div>
                <div class="relative max-h-48 h-full bg-color-blue-500 rounded-lg p-2 text-center flex items-end">
                <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-48</div>
                </div>
                <div class="relative max-h-40 h-full bg-color-blue-500 rounded-lg p-2 text-center flex items-end">
                <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-40</div>
                </div>
                <div class="relative max-h-32 h-full bg-color-blue-500 rounded-lg p-2 text-center flex items-end">
                <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-32</div>
                </div>
            </div>
            `
        },
{
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-6">
                Use <code class="text-color-gray-800 dark:text-color-white font-semibold">h-full max-h-full, h-full max-h-&lt;fraction&gt;</code>
                utilities like h-full max-h-1/2 and h-full max-h-2/5 to give an element a percentage-based height:
            </p>
            <div class="flex gap-4 justify-center items-end text-color-white font-mono font-bold h-96">
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-full max-h-full bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-full</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full max-h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-full max-h-9/10 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-9/10</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full max-h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-full max-h-3/4 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-3/4</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full max-h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-full max-h-1/2 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-1/2</div>
                    </div>
                </div>
                <div class="relative h-full flex items-end">
                    <div class="absolute inset-0 z-0">
                        <div class="h-full max-h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                    </div>
                    <div class="h-full max-h-1/3 bg-color-blue-500 rounded-lg text-center flex justify-center items-end w-8 relative">
                        <div class="mb-12 -rotate-90 text-left text-nowrap">max-h-1/3</div>
                    </div>
                </div>
            </div>
            `
        },
    ]
};
export default maxHeight;

<div className="sr-only max-h-xl max-h-lg max-h-3/4 max-h-1/2 max-h-1/3  max-h-9/10 min-w-xl max-h-[100px] max-h-lg max-h-md max-h-sm max-h-xs max-h-2xs max-h-3xs  max-h-full max-h-3/4 w-full !mb-0 space-y-4 w-60 w-20 max-h-96 max-h-80 max-h-64 max-h-48 max-h-40 max-h-32 max-h-24">

</div>